import { cxSingle, cxDefaultScoped, cxDynamic, CxGlobals, cxScoped, cxEvaluated, cxRecent } from '@proc7ts/context-values';
import { hasOwnProperty, superClassOf, elementOrArray, extendSetOfElements, setOfElements, mergeFunctions, noop, valueProvider, asis, valuesProvider, newPromiseResolver, valueByRecipe } from '@proc7ts/primitives';
import { amend, allAmender, AeClass, newAmendTarget, isAmendatory, AeMember } from '@proc7ts/amend';
import { isQualifiedName, NamespaceAliaser, html__naming, NamespaceDef } from '@frontmeans/namespace-aliaser';
import { DomEventDispatcher } from '@frontmeans/dom-events';
import { trackValue, mapAfter, digOn_, onceOn, AfterEvent__symbol, statePath, onEventBy, supplyOn, EventEmitter, valueOn, onAny, mapAfter_, filterOn_, translateOn, mapOn_, valueOn_, onPromise, StateTracker, ValueTracker, isEventSender, onSupplied, translateOn_ } from '@proc7ts/fun-events';
import { neverSupply, Supply } from '@proc7ts/supply';
import { CxWindow, PreRenderScheduler } from '@frontmeans/render-scheduler';
export { CxWindow as BootstrapWindow } from '@frontmeans/render-scheduler';
import { CxBuilder, cxConstAsset, CxPeerBuilder, cxBuildAsset } from '@proc7ts/context-builder';
import { DocumentRenderKit, drekBuild } from '@frontmeans/drek';
import { Workbench, WorkStage } from '@proc7ts/workbench';
import { CxModule } from '@proc7ts/context-modules';
import { hyphenateDecapName } from '@frontmeans/httongue';

/**
 * Context entry containing bootstrap context as its value.
 *
 * @category Core
 */
const BootstrapContext = {
    perContext: ( /*#__PURE__*/cxSingle()),
    toString: () => '[BootstrapContext]',
};

/**
 * @internal
 */
function mergeInitMethods(target1, method1, target2, method2) {
    const m1 = method1 && method1.bind(target1);
    const m2 = method2 && method2.bind(target2);
    if (!m2) {
        return m1;
    }
    if (!m1) {
        return m2;
    }
    return async (...args) => {
        await m1(...args);
        await m2(...args);
    };
}

/**
 * @category Utility
 */
class MetaAccessor {
    constructor(symbol) {
        this.symbol = symbol;
    }
    own(type, receiver) {
        return hasOwnProperty(type, this.symbol)
            ? Reflect.get(type, this.symbol, receiver)
            : undefined;
    }
    of(type, receiver = type) {
        const ownDef = this.own(type, receiver);
        const superType = superClassOf(type);
        const superDef = superType && this.of(superType, receiver);
        return ownDef ? (superDef ? this.merge([superDef, ownDef]) : ownDef) : superDef;
    }
    define(type, metas) {
        const prevMeta = this.own(type);
        const newMeta = this.merge(prevMeta ? [prevMeta, ...metas] : metas);
        Reflect.defineProperty(type, this.symbol, {
            configurable: true,
            value: newMeta,
        });
        return type;
    }
}

/**
 * A key of a property holding a feature definition within its class constructor.
 *
 * @category Core
 */
const FeatureDef__symbol = ( /*#__PURE__*/Symbol('FeatureDef'));
/**
 * @internal
 */
class FeatureMeta extends MetaAccessor {
    constructor() {
        super(FeatureDef__symbol);
    }
    merge(defs) {
        return defs.reduce((prev, def) => ({
            needs: elementOrArray(extendSetOfElements(setOfElements(prev.needs), def.needs)),
            has: elementOrArray(extendSetOfElements(setOfElements(prev.has), def.has)),
            setup: mergeInitMethods(prev, prev.setup, def, def.setup),
            init: mergeInitMethods(prev, prev.init, def, def.init),
        }), {});
    }
}
/**
 * @internal
 */
const featureMeta = ( /*#__PURE__*/new FeatureMeta());
/**
 * @category Core
 */
const FeatureDef = {
    /**
     * Extracts feature definition options from its type.
     *
     * @param featureType - Target feature class constructor.
     *
     * @returns Feature definition options. May be empty when there is no feature definition found in the given
     * `featureType`.
     */
    of(featureType) {
        return featureMeta.of(amend(featureType)) || {};
    },
    /**
     * Merges multiple feature definition options.
     *
     * @param defs - Feature definition options to merge.
     *
     * @returns Merged feature definition options.
     */
    merge(...defs) {
        return featureMeta.merge(defs);
    },
    /**
     * Defines a feature.
     *
     * Either creates new or extends an existing feature definition and stores it under `[FeatureDef__symbol]` key.
     *
     * @typeParam TClass - Feature type.
     * @param featureType - Feature class constructor.
     * @param defs - Feature definitions.
     *
     * @returns The `type` instance.
     */
    define(featureType, ...defs) {
        return featureMeta.define(featureType, defs);
    },
};

/**
 * Creates a feature class amendment (and decorator).
 *
 * Decorate a class with this decorator to define it as a feature like this:
 * ```TypeScript
 * @Feature({ needs: [OtherFeature, MyComponent] })
 * class MyFeature {
 *   // ...
 * }
 * ```
 *
 * Such feature can be passed to {@link bootstrapComponents} function or referenced by other features.
 *
 * This is an alternative to direct call to {@link FeatureDef.define} method.
 *
 * @category Core
 * @typeParam TClass - Amended feature class type.
 * @typeParam TAmended - Amended feature entity type.
 * @param amendments - Feature definitions or amendments to apply.
 *
 * @returns Feature class amendment and decorator.
 */
function Feature(...amendments) {
    const amender = Feature$toAmender(amendments);
    return AeClass(baseTarget => {
        let result = {};
        amender(newAmendTarget({
            base: {
                ...baseTarget,
                featureDef: {},
            },
            amend(_base, request = {}) {
                const { featureDef: defRequest = {}, ...baseRequest } = request;
                const createBaseTarget = baseTarget.amend(baseRequest);
                const featureDef = result = FeatureDef.merge(result, defRequest);
                return () => ({
                    ...createBaseTarget(),
                    featureDef,
                });
            },
        }));
        FeatureDef.define(baseTarget.amendedClass, result);
    });
}
function Feature$toAmender(amendments) {
    const featureDefs = [];
    const featureAmendments = [];
    for (const amendment of amendments) {
        if (isFeatureAmendment(amendment)) {
            featureAmendments.push(amendment);
        }
        else {
            featureDefs.push(amendment);
        }
    }
    if (featureDefs.length) {
        featureAmendments.push(FeatureDef$toAmender(featureDefs));
    }
    return allAmender(featureAmendments);
}
function isFeatureAmendment(amendment) {
    return typeof amendment === 'function' || isAmendatory(amendment);
}
function FeatureDef$toAmender(defs) {
    return ({ amend }) => amend({
        featureDef: FeatureDef.merge(...defs),
    });
}

/**
 * Feature context entry containing the feature context itself.
 *
 * @category Core
 */
const FeatureContext = {
    perContext: ( /*#__PURE__*/cxSingle()),
    toString: () => '[FeatureContext]',
};

const BootstrapWorkbench = {
    perContext: ( /*#__PURE__*/cxDefaultScoped(BootstrapContext, ( /*#__PURE__*/cxSingle({
        byDefault: () => new Workbench(),
    })))),
    toString: () => '[BootstrapWorkbench]',
};
const featureSetupStage = ( /*#__PURE__*/new WorkStage('feature setup'));
const featureInitStage = ( /*#__PURE__*/new WorkStage('feature init', { after: featureSetupStage }));
const componentDefStage = ( /*#__PURE__*/new WorkStage('component definition', { after: featureInitStage }));

/**
 * A key of a property holding a component definition within its class constructor.
 *
 * @category Core
 */
const ComponentDef__symbol = ( /*#__PURE__*/Symbol('ComponentDef'));
/**
 * @internal
 */
class ComponentMeta extends MetaAccessor {
    constructor() {
        super(ComponentDef__symbol);
    }
    merge(defs) {
        return defs.reduce((prev, meta) => {
            const def = this.meta(meta);
            return ({
                ...prev,
                ...def,
                setup: mergeFunctions(prev.setup, def.setup),
                define: mergeFunctions(prev.define, def.define),
                feature: prev.feature
                    ? def.feature ? FeatureDef.merge(prev.feature, def.feature) : prev.feature
                    : def.feature,
            });
        }, {});
    }
    meta(source) {
        if (isQualifiedName(source)) {
            return { name: source };
        }
        return source;
    }
}
/**
 * @internal
 */
const componentMeta = ( /*#__PURE__*/new ComponentMeta());
/**
 * @category Core
 */
const ComponentDef = {
    /**
     * Extracts component definition options from its type.
     *
     * @typeParam T - A type of component.
     * @param componentType - Target component class constructor.
     *
     * @returns Component definition options. May be empty if there is not definition attached to component type.
     */
    of(componentType) {
        return componentMeta.of(amend(componentType)) || {};
    },
    /**
     * Merges multiple component definition options.
     *
     * @typeParam T - A type of component.
     * @param defs - Component definition options to merge.
     *
     * @returns Merged component definition options.
     */
    merge(...defs) {
        return componentMeta.merge(defs);
    },
    /**
     * Defines a component.
     *
     * Either assigns new or extends existing component definition and stores it under {@link ComponentDef__symbol} key.
     *
     * Each component can be passed directly to {@link bootstrapComponents} function or added as a requirement
     * of another feature.
     *
     * @typeParam TClass - A type of component class.
     * @param componentType - Component class constructor.
     * @param defs - Component definitions.
     *
     * @returns The `type` instance.
     */
    define(componentType, ...defs) {
        return componentMeta.define(componentType, defs);
    },
};

/**
 * Creates a component class amendment (and decorator).
 *
 * Decorated class becomes component:
 * ```TypeScript
 * @Component({ name: 'my-element' })
 * class MyComponent {
 *   // ...
 * }
 * ```
 *
 * Such component can be registered with {@link FeatureContext.define} method or used as a feature, e.g. passed to
 * {@link bootstrapComponents} function, or added to {@link FeatureDef.needs} property of another feature.
 *
 * This is an alternative to direct call to {@link ComponentDef.define} method.
 *
 * @category Core
 * @typeParam TClass - Amended component class type.
 * @typeParam TAmended - Amended component entity type.
 * @param amendments - Component definitions, qualified name od component's element, or amendments to apply.
 *
 * @returns Component class amendment and decorator.
 */
function Component(...amendments) {
    const amender = Component$toAmender(amendments);
    return Feature(baseTarget => {
        let result = {};
        amender(newAmendTarget({
            base: {
                ...baseTarget,
                componentDef: {},
            },
            amend(_base, request = {}) {
                const { componentDef: defRequest = {}, ...baseRequest } = request;
                const createBaseTarget = baseTarget.amend(baseRequest);
                const componentDef = result = ComponentDef.merge(result, defRequest);
                return () => ({
                    ...createBaseTarget(),
                    componentDef,
                });
            },
        }));
        ComponentDef.define(baseTarget.amendedClass, result);
    });
}
function Component$toAmender(amendments) {
    const componentDefs = [];
    const componentAmendments = [];
    for (const amendment of amendments) {
        if (isComponentAmendment(amendment)) {
            componentAmendments.push(amendment);
        }
        else {
            componentDefs.push(amendment);
        }
    }
    if (componentDefs.length) {
        componentAmendments.push(ComponentDef$toAmender(componentDefs));
    }
    return allAmender(componentAmendments);
}
function isComponentAmendment(amendment) {
    return typeof amendment === 'function' || isAmendatory(amendment);
}
function ComponentDef$toAmender(defs) {
    return ({ amend }) => amend({
        componentDef: ComponentDef.merge(...defs),
    });
}

/**
 * A key of component instance method returning its component context.
 *
 * @category Core
 */
const ComponentContext__symbol = ( /*#__PURE__*/Symbol('ComponentContext'));
/**
 * Component context entry containing the context itself.
 *
 * @category Core
 */
const ComponentContext = {
    perContext: ( /*#__PURE__*/cxSingle()),
    /**
     * Extracts component context from the given component instance.
     *
     * @param component - Target component instance.
     *
     * @return Component context reference returned by {@link ComponentContext__symbol} method.
     *
     * @throws TypeError  When the given `component` does not contain component context reference.
     */
    of(component) {
        if (typeof component[ComponentContext__symbol] !== 'function') {
            throw new TypeError(`No component context found in ${String(component)}`);
        }
        return component[ComponentContext__symbol]();
    },
    toString: () => '[ComponentContext]',
};

/**
 * Component context entry containing component event dispatcher.
 *
 * @category Core
 */
const ComponentEventDispatcher = {
    perContext: ( /*#__PURE__*/cxSingle({
        byDefault(values) {
            const context = values.get(ComponentContext);
            const dispatcher = new DomEventDispatcher(context.element);
            dispatcher.supply.needs(context);
            return {
                dispatch(event) {
                    return dispatcher.dispatch(event);
                },
                on(type) {
                    return dispatcher.on(type);
                },
            };
        },
    })),
    toString: () => '[ComponentEventDispatcher]',
};

/**
 * Creates an amendment (and decorator) for the component instance member.
 *
 * @category Core
 * @typeParam TValue - Amended member value type.
 * @typeParam TClass - Amended component class type.
 * @typeParam TUpdate - Amended member update type accepted by its setter.
 * @typeParam TAmended - Amended component member entity type.
 * @param amendments - Amendments to apply.
 *
 * @returns New component member amendment instance.
 */
function ComponentMember(...amendments) {
    return AeMember(Component(allAmender(amendments)));
}

const ComponentSlot$empty = {
    get: noop,
    unbind: noop,
    rebind: noop,
    drop: noop,
};
/**
 * @internal
 */
class ComponentSlot$ {
    constructor() {
        this._provider = trackValue(ComponentSlot$empty);
        this.read = this._provider.read.do(mapAfter(provider => provider.get()));
        this.whenReady = this.read.do(digOn_(ctx => ctx && ctx.whenReady), onceOn);
    }
    get context() {
        return this._provider.it.get();
    }
    [AfterEvent__symbol]() {
        return this.read;
    }
    bind(context) {
        this._provider.it.drop();
        this._provider.it = ComponentSlot$known(this, context);
    }
    bindBy(binder) {
        this._provider.it.drop();
        this._provider.it = ComponentSlot$bound(this, binder);
    }
    unbind() {
        this._provider.it.unbind();
    }
    rebind() {
        return this.context || this._provider.it.rebind();
    }
}
function ComponentSlot$known(slot, context) {
    context.supply.whenOff(() => {
        if (slot.context === context) {
            slot.unbind();
        }
    });
    const get = () => context;
    return {
        get,
        unbind() {
            slot._provider.it = ComponentSlot$empty;
        },
        rebind: get,
        drop: noop,
    };
}
function ComponentSlot$bound(slot, binder) {
    let supply = neverSupply();
    let getContext = noop;
    const get = () => getContext();
    const newSupply = () => supply = new Supply(() => {
        getContext = noop;
    });
    let bind = (context) => {
        getContext = valueProvider(context);
        context.supply.whenOff(() => {
            if (slot.context === context) {
                slot.unbind();
            }
        });
        return newSupply();
    };
    const drop = () => {
        bind = _ => neverSupply();
        supply.off();
    };
    const unbind = () => {
        supply.off();
        slot._provider.it = {
            get,
            unbind,
            rebind,
            drop,
        };
    };
    const bindContext = () => binder({
        bind: context => bind(context),
    });
    getContext = () => {
        bindContext();
        // Subsequent bind calls update the component provider
        bind = context => {
            supply.off();
            getContext = valueProvider(context);
            slot._provider.it = {
                get,
                unbind,
                rebind,
                drop,
            };
            return newSupply();
        };
        return getContext();
    };
    const rebind = () => {
        bindContext();
        return getContext();
    };
    return {
        get,
        unbind,
        rebind,
        drop,
    };
}

/**
 * A key of component element property containing a reference to component slot.
 *
 * @category Core
 */
const ComponentSlot__symbol = ( /*#__PURE__*/Symbol('ComponentSlot'));
/**
 * @category Core
 */
const ComponentSlot = {
    /**
     * Accesses a component slot of the given element. Attaches a new slot if necessary.
     *
     * @param element - Target element.
     *
     * @returns A component slot instance attached to the element.
     */
    of(element) {
        const found = element[ComponentSlot__symbol];
        if (found) {
            return found;
        }
        return element[ComponentSlot__symbol] = new ComponentSlot$();
    },
};

/**
 * Component context entry containing a component root element.
 *
 * This is an element itself by default. But can be overridden e.g. by {@link AttachShadow @AttachShadow} decorator.
 *
 * @category Core
 */
const ContentRoot = {
    perContext: ( /*#__PURE__*/cxSingle({
        byDefault(target) {
            return target.get(ComponentContext).element;
        },
    })),
    toString: () => '[ContentRoot]',
};

/**
 * Component context entry containing a component {@link StateUpdater state updater} function.
 *
 * This function is called by default when component state changes, e.g. attribute value or DOM property modified.
 *
 * Does nothing by default and after component destruction.
 *
 * When multiple state updaters provided, they all will be called on each state update, in reverse order.
 *
 * @category Core
 */
const StateUpdater = {
    perContext: ( /*#__PURE__*/cxDynamic({
        create: asis,
        byDefault: valuesProvider(),
        assign: ({ get, to }, { supply }) => {
            let update = (path, newValue, oldValue) => {
                path = statePath(path);
                const updaters = get();
                for (let i = updaters.length - 1; i >= 0; --i) {
                    updaters[i](path, newValue, oldValue);
                }
            };
            const updater = (path, newValue, oldValue) => update(path, newValue, oldValue);
            let assigner = receiver => to((_, by) => receiver(updater, by));
            supply.whenOff(() => {
                update = StateUpdater$noop;
                assigner = StateUpdater$noop$assigner;
            });
            return receiver => assigner(receiver);
        },
    })),
    toString: () => '[StateUpdater]',
};
function StateUpdater$noop$assigner(receiver) {
    receiver(StateUpdater$noop);
}
function StateUpdater$noop(_path, _newValue, _oldValue) {
    // Do not update the state.
}

const BootstrapContextBuilder$perContext = ( /*#__PURE__*/cxSingle());
class BootstrapContextBuilder extends CxBuilder {
    static perContext(target) {
        return BootstrapContextBuilder$perContext(target);
    }
    static toString() {
        return '[BootstrapContextBuilder]';
    }
    constructor(createContext) {
        super(createContext);
        this.provide(cxConstAsset(BootstrapContextBuilder, this));
        this.provide(cxConstAsset(CxGlobals, this.context));
        this.provide(cxConstAsset(BootstrapContext, this.context));
    }
}

function onPostDefSetup(componentType, supply) {
    const { on } = postDefSetup(componentType);
    return onEventBy(receiver => {
        on({
            supply: receiver.supply.needs(supply),
            receive(ctx, setup) {
                const whenReady = setup.whenReady.do(supplyOn(supply));
                const whenComponent = setup.whenComponent.do(supplyOn(supply));
                receiver.receive(ctx, {
                    get componentType() {
                        return setup.componentType;
                    },
                    get whenReady() {
                        return whenReady;
                    },
                    get whenComponent() {
                        return whenComponent;
                    },
                    perDefinition(asset) {
                        return setup.perDefinition(asset).needs(supply);
                    },
                    perComponent(asset) {
                        return setup.perComponent(asset).needs(supply);
                    },
                });
            },
        });
    });
}
const PostDefSetup__symbol = ( /*#__PURE__*/Symbol('PostDefSetup'));
function postDefSetup(componentType) {
    if (hasOwnProperty(componentType, PostDefSetup__symbol)) {
        return componentType[PostDefSetup__symbol];
    }
    const tracker = trackValue();
    const emitter = new EventEmitter();
    const onSetup = tracker.read.do(valueOn(asis));
    const on = onAny(onSetup, emitter);
    const superType = superClassOf(componentType, type => ComponentDef__symbol in type);
    if (superType) {
        const superPostDefSetup = postDefSetup(superType);
        on(setup => superPostDefSetup.send(setup));
    }
    const result = {
        on,
        send(setup) {
            emitter.send(setup);
        },
        setup(setup) {
            tracker.it = setup;
        },
    };
    Reflect.defineProperty(componentType, PostDefSetup__symbol, { value: result });
    return result;
}

/**
 * Bootstrap context entry containing bootstrap root as its value.
 *
 * Target value defaults to document body of {@link BootstrapWindow}.
 *
 * @category Core
 */
const BootstrapRoot = {
    perContext: ( /*#__PURE__*/cxSingle({
        byDefault: target => target.get(CxWindow).document.body,
    })),
    toString: () => '[BootstrapRoot]',
};

/**
 * Bootstrap context entry containing element naming service instance.
 *
 * @category Core
 */
const ElementNaming = {
    perContext: ( /*#__PURE__*/cxScoped(BootstrapContext, ( /*#__PURE__*/cxSingle({
        byDefault: ElementNaming$byDefault,
    })))),
    toString: () => '[ElementNaming]',
};
function ElementNaming$byDefault(target) {
    const bsWindow = target.get(CxWindow);
    const nsAlias = target.get(NamespaceAliaser);
    return {
        elementOf(componentType) {
            const { name, extend } = ComponentDef.of(componentType);
            let tagName;
            const elementExtend = {
                get type() {
                    return extend && extend.type || bsWindow.HTMLElement;
                },
                get name() {
                    return extend && extend.name;
                },
            };
            return {
                get name() {
                    return name;
                },
                get tagName() {
                    return tagName || (name && (tagName = html__naming.name(name, nsAlias)));
                },
                get extend() {
                    return elementExtend;
                },
            };
        },
    };
}

/**
 * Default Wesib namespace definition.
 *
 * @category Core
 */
const Wesib__NS = ( /*#__PURE__*/new NamespaceDef('https://wesib.github.io/ns', 'b', 'wesib'));

const DefinitionContext__symbol = ( /*#__PURE__*/Symbol('DefinitionContext'));
function definitionContextOf(componentType) {
    if (!hasOwnProperty(componentType, DefinitionContext__symbol)) {
        throw new TypeError(`Component is not defined: ${componentType}`);
    }
    return componentType[DefinitionContext__symbol];
}

/**
 * Context entry containing custom elements registry to use.
 *
 * @category Core
 */
const CustomElements = {
    perContext: ( /*#__PURE__*/cxDefaultScoped(BootstrapContext, ( /*#__PURE__*/cxSingle({
        byDefault: CustomElements$byDefault,
    })))),
    toString: () => `[CustomElements]`,
};
function CustomElements$byDefault(target) {
    const customElements = target.get(CxWindow).customElements;
    const nsAlias = target.get(NamespaceAliaser);
    class CustomElements$ {
        define(componentTypeOrName, elementType) {
            if (isQualifiedName(componentTypeOrName)) {
                customElements.define(html__naming.name(componentTypeOrName, nsAlias), elementType);
                return;
            }
            const defContext = definitionContextOf(componentTypeOrName);
            const { tagName, extend } = defContext.elementDef;
            if (!tagName) {
                CustomComponent$resolver(componentTypeOrName).resolve(undefined);
                return; // Anonymous component.
            }
            if (extend && extend.name) {
                customElements.define(tagName, elementType, {
                    extends: extend.name,
                });
            }
            else {
                customElements.define(tagName, elementType);
            }
        }
        whenDefined(componentTypeOrName) {
            if (isQualifiedName(componentTypeOrName)) {
                return customElements.whenDefined(html__naming.name(componentTypeOrName, nsAlias));
            }
            const defContext = definitionContextOf(componentTypeOrName);
            const { name } = defContext.elementDef;
            if (!name) {
                return CustomComponent$resolver(componentTypeOrName).promise();
            }
            return customElements.whenDefined(html__naming.name(name, nsAlias));
        }
    }
    return new CustomElements$();
}
const CustomComponent$resolver__symbol = ( /*#__PURE__*/Symbol('CustomComponent.resolver'));
function CustomComponent$resolver(componentType) {
    if (hasOwnProperty(componentType, CustomComponent$resolver__symbol)) {
        return componentType[CustomComponent$resolver__symbol];
    }
    return componentType[CustomComponent$resolver__symbol] = newPromiseResolver();
}

/**
 * Context entry containing definition context as its value.
 *
 * @category Core
 */
const DefinitionContext = {
    perContext: ( /*#__PURE__*/cxSingle()),
    toString: () => '[DefinitionContext]',
};

const ComponentConstructor__symbol = ( /*#__PURE__*/Symbol('newComponent'));
/**
 * @internal
 */
function newComponent(context) {
    const componentType = context.componentType;
    if (!componentType[ComponentConstructor__symbol]) {
        componentType[ComponentConstructor__symbol] = newComponentConstructor(componentType);
    }
    return componentType[ComponentConstructor__symbol](context);
}
function newComponentConstructor(componentType) {
    // Component context reference specific to component class.
    const context__symbol = Symbol('ComponentContext');
    let defaultContext;
    componentType.prototype[ComponentContext__symbol] = function () {
        return this[context__symbol] || (this[context__symbol] = defaultContext);
    };
    return function (context) {
        const prevContext = defaultContext;
        // Ensure the component context is available in component constructor.
        defaultContext = context;
        try {
            const component = new this(context);
            component[context__symbol] = context;
            return component;
        }
        finally {
            defaultContext = prevContext;
        }
    };
}

/**
 * @internal
 */
class ComponentStatus {
    constructor(_ctx) {
        this._ctx = _ctx;
        this._val = trackValue(0 /* Building */);
        this._canSettle = 0;
        this.supply.cuts(this._val);
    }
    get supply() {
        return this._ctx.supply;
    }
    read() {
        return (this.read = valueProvider(this._val.read.do(mapAfter_(valueProvider(this._ctx)))))();
    }
    isReady() {
        return !!this._val.it && !this._val.supply.isOff;
    }
    onceReady() {
        return (this.onceReady = valueProvider(this.read().do(ComponentStatus$once(({ ready }) => ready))))();
    }
    whenReady() {
        return (this.whenReady = valueProvider(this.onceReady().do(onceOn)))();
    }
    isSettled() {
        return this._val.it >= 2 /* Settled */ && !this._val.supply.isOff;
    }
    onceSettled() {
        return (this.onceSettled = valueProvider(this.read().do(ComponentStatus$once(({ settled }) => settled))))();
    }
    whenSettled() {
        return (this.whenSettled = valueProvider(this.onceSettled().do(onceOn)))();
    }
    isConnected() {
        return this._val.it >= 3 /* Connected */ && !this._val.supply.isOff;
    }
    onceConnected() {
        return (this.onceConnected = valueProvider(this.read().do(
        // Filtering is enough, as there is no status after "connected"
        filterOn_(({ connected }) => connected))))();
    }
    whenConnected() {
        return (this.whenConnected = valueProvider(this.onceConnected().do(onceOn)))();
    }
    ready() {
        this._val.it = 1 /* Ready */;
    }
    settle() {
        if (this._canSettle && this._val.it < 2 /* Settled */) {
            // Prevent settling until exiting custom element constructor
            this._val.it = 2 /* Settled */;
        }
    }
    connect() {
        this._val.it = 3 /* Connected */;
    }
    create() {
        this._canSettle = 1; // Can settle now
    }
}
function ComponentStatus$once(test) {
    return input => onEventBy(receiver => {
        let value = false;
        input({
            supply: receiver.supply,
            receive(eventCtx, componentCtx) {
                const newValue = test(componentCtx);
                if (newValue && !value) {
                    value = newValue;
                    receiver.receive(eventCtx, componentCtx);
                }
            },
        });
    });
}

const PerComponentCxPeer = {
    perContext: ( /*#__PURE__*/cxScoped(BootstrapContext, ( /*#__PURE__*/cxEvaluated(_target => new CxPeerBuilder())))),
    toString: () => '[PerComponentCxPeer]',
};
class ComponentContext$ {
    constructor(_defContext, _builder, element, get) {
        this._defContext = _defContext;
        this._builder = _builder;
        this.element = element;
        this.get = get;
        this.updateState = (key, newValue, oldValue) => {
            this.get(StateUpdater)(key, newValue, oldValue);
        };
        this._status = new ComponentStatus(this);
        this.supply.whenOff(() => {
            delete this.component[ComponentContext__symbol];
            this._component = componentDestroyed;
        });
    }
    get componentType() {
        return this._defContext.componentType;
    }
    get component() {
        return this._component();
    }
    get supply() {
        return this._builder.supply;
    }
    get ready() {
        return this._status.isReady();
    }
    get onceReady() {
        return this._status.onceReady();
    }
    get whenReady() {
        return this._status.whenReady();
    }
    get settled() {
        return this._status.isSettled();
    }
    get onceSettled() {
        return this._status.onceSettled();
    }
    get whenSettled() {
        return this._status.whenSettled();
    }
    get connected() {
        return this._status.isConnected();
    }
    get onceConnected() {
        return this._status.onceConnected();
    }
    get whenConnected() {
        return this._status.whenConnected();
    }
    get readStatus() {
        return this._status.read();
    }
    get contentRoot() {
        return this.get(ContentRoot);
    }
    _component() {
        throw new TypeError('Component is not constructed yet. Consider to use a `whenReady()` callback');
    }
    settle() {
        this._status.settle();
    }
    on(type) {
        return this.get(ComponentEventDispatcher).on(type);
    }
    dispatchEvent(event) {
        this.get(ComponentEventDispatcher).dispatch(event);
    }
    _createComponent() {
        const whenComponent = this._defContext._whenComponent;
        let lastRev = 0;
        whenComponent.readNotifier.do(onceOn)(notifier => lastRev = notifier(this, lastRev));
        this.whenConnected(() => {
            whenComponent.readNotifier({
                supply: new Supply().needs(this),
                receive: (_, notifier) => {
                    lastRev = notifier(this, lastRev);
                },
            });
        });
        this._defContext._elementBuilder.components.send(this);
        const component = newComponent(this);
        this._component = valueProvider(component);
        this._status.ready();
        return this;
    }
    _connect() {
        this._status.connect();
    }
    _created() {
        this._status.create();
    }
}
class ComponentContext$Mounted extends ComponentContext$ {
    static create(defContext, element) {
        return defContext._newComponentContext((get, builder) => new ComponentContext$Mounted(defContext, builder, element, get));
    }
    get mounted() {
        return true;
    }
}
function componentDestroyed() {
    throw new TypeError('Component destroyed already');
}

class ComponentContext$Custom extends ComponentContext$ {
    static create(defContext, element) {
        return defContext._newComponentContext((get, builder) => new ComponentContext$Custom(defContext, builder, element, get));
    }
    get mounted() {
        return false;
    }
}
function customElementType(defContext) {
    const { elementDef } = defContext;
    const renderKit = defContext.get(DocumentRenderKit);
    class CustomElement$ extends elementDef.extend.type {
        constructor() {
            super();
            const slot = ComponentSlot.of(this);
            // Ignore immediate settlement, as is typically leads to DOM manipulations prohibited inside constructor.
            let settle = noop;
            slot.bindBy(({ bind }) => {
                const context = ComponentContext$Custom.create(defContext, this);
                const supply = bind(context);
                context._createComponent();
                context._created();
                context.supply.needs(supply).whenOff(() => {
                    renderKit.contextOf(this).whenSettled(_ => settle());
                });
            });
            renderKit.contextOf(this).whenSettled(_ => settle());
            // Assume settlement happens after constructor completion.
            settle = () => slot.rebind().settle();
        }
        connectedCallback() {
            var _a;
            (_a = super.connectedCallback) === null || _a === void 0 ? void 0 : _a.call(this);
            ComponentSlot.of(this).rebind()._connect();
        }
        disconnectedCallback() {
            var _a;
            ComponentSlot.of(this).context.supply.off();
            (_a = super.disconnectedCallback) === null || _a === void 0 ? void 0 : _a.call(this);
        }
    }
    return CustomElement$;
}

class WhenComponent {
    constructor() {
        const currentRev = trackValue(0);
        const created = new EventEmitter();
        this.onCreated = onEventBy(receiver => {
            const receiverRev = currentRev.it + 1;
            created.on({
                supply: receiver.supply,
                receive: (eventContext, componentContext, notifiedRev) => {
                    if (notifiedRev < receiverRev) {
                        // Notify only receivers added after the last notification
                        receiver.receive({
                            onRecurrent(recurrentReceiver) {
                                eventContext.onRecurrent(recurrentContext => recurrentReceiver(recurrentContext));
                            },
                        }, componentContext);
                    }
                },
            });
            ++currentRev.it;
        });
        this.readNotifier = currentRev.read.do(mapAfter_(rev => (context, notifiedRev) => {
            created.send(context, notifiedRev);
            return rev;
        }));
    }
}

const PerDefinitionCxPeer = {
    perContext: ( /*#__PURE__*/cxScoped(BootstrapContext, ( /*#__PURE__*/cxEvaluated(_target => new CxPeerBuilder())))),
    toString: () => '[PerDefinitionCxPeer]',
};
class DefinitionContext$ {
    constructor(_bsContext, _elementBuilder, componentType, _cxBuilder, get) {
        var _a, _b;
        this._bsContext = _bsContext;
        this._elementBuilder = _elementBuilder;
        this.componentType = componentType;
        this._cxBuilder = _cxBuilder;
        this.get = get;
        this._whenComponent = new WhenComponent();
        this._ready = trackValue(false);
        this._whenReady = this._ready.read.do(translateOn((send, ready) => ready && send()));
        this._def = ComponentDef.of(componentType);
        this.elementDef = _bsContext.get(ElementNaming).elementOf(componentType);
        this._perComponentCxPeer = new CxPeerBuilder(_bsContext.get(PerComponentCxPeer));
        this.whenReady = this._whenReady.do(mapOn_(valueProvider(this)), onceOn);
        const definitionSetup = {
            get componentType() {
                return componentType;
            },
            whenReady: this.whenReady,
            whenComponent: this.whenComponent,
            perDefinition: asset => _cxBuilder.provide(asset),
            perComponent: asset => this._perComponentCxPeer.provide(asset),
        };
        (_b = (_a = this._def).setup) === null || _b === void 0 ? void 0 : _b.call(_a, definitionSetup);
        postDefSetup(componentType).setup(definitionSetup);
    }
    static create(bsContext, elementBuilder, componentType) {
        const cxBuilder = new CxBuilder((get, builder) => new DefinitionContext$(bsContext, elementBuilder, componentType, builder, get), bsContext.get(BootstrapContextBuilder).boundPeer, bsContext.get(PerDefinitionCxPeer));
        const context = cxBuilder.context;
        cxBuilder.provide(cxConstAsset(DefinitionContext, context));
        return context;
    }
    get elementType() {
        return this._elementType();
    }
    get whenComponent() {
        return this._whenComponent.onCreated;
    }
    mountTo(element) {
        const context = ComponentContext$Mounted.create(this, element);
        ComponentSlot.of(element).bind(context);
        context._createComponent();
        const drekContext = context.get(DocumentRenderKit).contextOf(element);
        drekContext.whenSettled(_ => context.settle()).needs(context);
        drekContext.whenConnected(_ => context._connect()).needs(context);
        context._created();
        return context;
    }
    perComponent(asset) {
        return this._perComponentCxPeer.provide(asset);
    }
    _newComponentContext(createContext) {
        const builder = new CxBuilder(createContext, this._cxBuilder.boundPeer, this._perComponentCxPeer);
        const context = builder.context;
        builder.provide(cxConstAsset(ComponentContext, context));
        return context;
    }
    _elementType() {
        throw new TypeError('Custom element class is not constructed yet. Consider to use a `whenReady()` callback');
    }
    _define() {
        var _a, _b;
        (_b = (_a = this._def).define) === null || _b === void 0 ? void 0 : _b.call(_a, this);
        this._elementBuilder.definitions.send(this);
        this._elementType = valueProvider(customElementType(this));
        this.componentType[DefinitionContext__symbol] = this;
        this._ready.it = true;
    }
}

const ElementBuilder = {
    perContext: ( /*#__PURE__*/cxDefaultScoped(BootstrapContext, ( /*#__PURE__*/cxSingle({
        byDefault: ElementBuilder$byDefault,
    })))),
    toString: () => '[ElementBuilder]',
};
function ElementBuilder$byDefault(target) {
    return {
        definitions: new EventEmitter(),
        components: new EventEmitter(),
        buildElement(componentType) {
            const definitionContext = DefinitionContext$.create(target.get(BootstrapContext), this, componentType);
            definitionContext._define();
            return definitionContext;
        },
    };
}

/**
 * @internal
 */
class ComponentRegistry {
    constructor(_setup) {
        this._setup = _setup;
        this._components = undefined;
    }
    define(componentType) {
        if (this._components) {
            this._components.push(componentType);
        }
        else {
            this._components = [componentType];
            this._defineAll(this._components);
        }
    }
    _defineAll(components) {
        const workbench = this._setup.get(BootstrapWorkbench);
        const whenDefined = workbench.work(componentDefStage).run(() => {
            const customElements = this._setup.get(CustomElements);
            const elementBuilder = this._setup.get(ElementBuilder);
            components.forEach(componentType => {
                const defContext = elementBuilder.buildElement(componentType);
                customElements.define(componentType, defContext.elementType);
            });
            this._components = undefined;
        });
        this._setup.initBy(valueProvider(whenDefined));
    }
}

class FeatureContext$ {
    constructor(feature, get, _setup) {
        this.feature = feature;
        this.get = get;
        this._setup = _setup;
        this._bsContext = _setup.get(BootstrapContext);
        const handle = _setup.get(_setup.module);
        this.whenReady = handle.read.do(valueOn_(({ ready }) => ready && this), onceOn);
        this._componentRegistry = new ComponentRegistry(this._setup);
    }
    static create(feature, setup) {
        const bsBuilder = setup.get(BootstrapContextBuilder);
        const builder = new CxBuilder(get => new FeatureContext$(feature, get, setup), bsBuilder.boundPeer);
        const context = builder.context;
        builder.provide(cxConstAsset(FeatureContext, context));
        return context;
    }
    get supply() {
        return this._setup.supply;
    }
    get onDefinition() {
        return this._onDefinition
            || (this._onDefinition = this._setup.get(ElementBuilder).definitions.on.do(supplyOn(this)));
    }
    get onComponent() {
        return this._onComponent
            || (this._onComponent = this._setup.get(ElementBuilder).components.on.do(supplyOn(this)));
    }
    provide(asset) {
        return this._bsContext
            .get(BootstrapContextBuilder)
            .provide(asset)
            .needs(this);
    }
    perDefinition(asset) {
        return this._bsContext
            .get(PerDefinitionCxPeer)
            .provide(asset)
            .needs(this);
    }
    perComponent(asset) {
        return this._bsContext
            .get(PerComponentCxPeer)
            .provide(asset)
            .needs(this);
    }
    setupDefinition(componentType) {
        return onPostDefSetup(componentType, this.supply);
    }
    define(componentType) {
        this._componentRegistry.define(componentType);
    }
    whenDefined(componentType) {
        return this.get(BootstrapContext).whenDefined(componentType);
    }
    load(feature, user) {
        return this.get(BootstrapContext).load(feature, user ? new Supply().needs(this).needs(user) : this);
    }
}

const FeatureModule__symbol = ( /*#__PURE__*/Symbol('FeatureModule'));
class FeatureModule extends CxModule {
    constructor(feature) {
        super(feature.name, FeatureModule$options(feature));
        this.feature = feature;
    }
    static of(feature) {
        if (hasOwnProperty(feature, FeatureModule__symbol)) {
            return feature[FeatureModule__symbol];
        }
        return feature[FeatureModule__symbol] = new FeatureModule(feature);
    }
    async setup(setup) {
        const workbench = setup.get(BootstrapWorkbench);
        await workbench.work(featureSetupStage).run(() => super.setup(setup));
    }
    toString() {
        return `[Feature ${this.name}]`;
    }
}
function FeatureModule$options(feature) {
    const def = featureDef(feature);
    const has = [];
    const needs = [];
    for (const replaced of setOfElements(def.has)) {
        has.push(FeatureModule.of(replaced));
    }
    for (const required of setOfElements(def.needs)) {
        needs.push(FeatureModule.of(required));
    }
    return {
        needs,
        has,
        async setup(setup) {
            var _a;
            const workbench = setup.get(BootstrapWorkbench);
            const featureContext = FeatureContext$.create(feature, setup);
            if (def.init) {
                const whenInit = workbench.work(featureInitStage).run(async () => {
                    await def.init(featureContext);
                });
                setup.initBy(valueProvider(whenInit));
            }
            await ((_a = def.setup) === null || _a === void 0 ? void 0 : _a.call(def, featureContext));
        },
    };
}
function featureDef(featureType) {
    let def = FeatureDef.of(featureType);
    if (ComponentDef__symbol in featureType) {
        def = FeatureDef.merge(def, {
            init(context) {
                context.define(featureType);
            },
        });
        const { feature } = ComponentDef.of(featureType);
        if (feature) {
            def = FeatureDef.merge(def, feature);
        }
    }
    return def;
}

/**
 * @internal
 */
const WhenDefined__symbol = ( /*#__PURE__*/Symbol('WhenDefined'));
/**
 * @internal
 */
function whenDefined(bsContext, componentType) {
    if (hasOwnProperty(componentType, WhenDefined__symbol)) {
        return componentType[WhenDefined__symbol];
    }
    const result = onPromise(Promise.resolve(bsContext.whenReady)
        .then(() => bsContext.get(CustomElements).whenDefined(componentType))
        .then(() => definitionContextOf(componentType)));
    return componentType[WhenDefined__symbol] = result;
}

/**
 * Bootstraps components.
 *
 * Both features and components can be passed as parameters to this function.
 *
 * @category Core
 * @param features - Features and components to enable.
 *
 * @returns Bootstrap context instance.
 */
function bootstrapComponents(...features) {
    const { bsContext, complete } = initBootstrap();
    const feature = features.length === 1 ? features[0] : bootstrapFeature(features);
    bsContext.load(feature)
        .read
        .do(valueOn_(({ ready }) => ready), onceOn)
        .then(complete)
        .catch(console.error);
    return bsContext;
}
function bootstrapFeature(needs) {
    return FeatureDef.define(class BootstrapFeature {
    }, { needs });
}
function initBootstrap() {
    const stage = trackValue(0 /* Init */);
    const bsBuilder = new BootstrapContextBuilder(getValue => {
        class BootstrapContext$ {
            constructor() {
                this.whenReady = stage.read.do(valueOn(bsStage => !!bsStage && this), onceOn);
            }
            get(entry, request) {
                return getValue(entry, request);
            }
            whenDefined(componentType) {
                return whenDefined(this, componentType);
            }
            load(feature, user) {
                const module = FeatureModule.of(feature);
                const supply = bsBuilder.provide(module);
                if (user) {
                    supply.needs(user);
                }
                else {
                    user = supply;
                }
                const use = this.get(module).use(user);
                const read = FeatureRef$read(feature, use);
                return {
                    read,
                    whenReady: read.do(valueOn_(status => status.ready && status), onceOn),
                    [AfterEvent__symbol]: valueProvider(read),
                    supply,
                };
            }
        }
        return new BootstrapContext$();
    });
    const bsContext = bsBuilder.context;
    return {
        bsContext,
        complete() {
            stage.it = 1 /* Ready */;
        },
    };
}
function FeatureRef$read(feature, use) {
    const status = trackValue({ feature, ready: false });
    use.read(({ module, ready }) => {
        const feature = module.feature;
        const lastStatus = status.it;
        if (!lastStatus || lastStatus.feature !== feature || lastStatus.ready !== ready) {
            status.it = {
                feature,
                ready,
            };
        }
    }).needs(use);
    status.supply.needs(use);
    return status.read;
}

/**
 * A path to sub-state containing element an attributes.
 *
 * Thus, an attribute state path is always something like `[AttributePath__root, 'attribute-name']`.
 *
 * @category Feature
 */
const AttributePath__root = ( /*#__PURE__*/Symbol('attribute'));
/**
 * Constructs a named attribute state path.
 *
 * @category Feature
 * @param name - Target attribute name.
 *
 * @return Attribute state path.
 */
function attributePathTo(name) {
    return [AttributePath__root, name];
}

/**
 * @internal
 */
function attributeStateUpdate(name, updateState = true) {
    if (updateState === false) {
        return noop;
    }
    if (updateState === true || typeof updateState === 'function') {
        const path = attributePathTo(name);
        const update = updateState === true ? updateAttributeState : updateState;
        return (component, newValue, oldValue) => update(component, path, newValue, oldValue);
    }
    return (component, newValue, oldValue) => updateAttributeState(component, updateState, newValue, oldValue);
}
function updateAttributeState(component, path, newValue, oldValue) {
    ComponentContext.of(component).updateState(path, newValue, oldValue);
}

/**
 * @internal
 */
function parseAttributeDescriptor(target, propertyKey, def) {
    let name;
    let change;
    if (typeof def === 'string') {
        name = hyphenateDecapName(def);
        change = attributeStateUpdate(name);
    }
    else {
        if (def && def.name) {
            name = hyphenateDecapName(def.name);
        }
        else if (typeof propertyKey !== 'string') {
            throw new TypeError('Attribute name is required as property key is not a string: '
                + `${target.constructor.name}.prototype.${String(propertyKey)}`);
        }
        else {
            name = hyphenateDecapName(propertyKey);
        }
        change = attributeStateUpdate(name, def && def.updateState);
    }
    return { name, change };
}

/**
 * Component definition context entry containing {@link AttributeRegistry attribute registry}.
 *
 * @category Feature
 */
const AttributeRegistry = {
    perContext: ( /*#__PURE__*/cxScoped(DefinitionContext, ( /*#__PURE__*/cxSingle({
        byDefault: target => new AttributeRegistry$(target.get(DefinitionContext)),
    })))),
    toString: () => '[AttributeRegistry]',
};
class AttributeRegistry$ {
    constructor(_defContext) {
        this._defContext = _defContext;
        this.attrs = new Map();
        _defContext.whenReady(({ elementType }) => this.define(elementType));
        _defContext.whenComponent(context => {
            if (context.mounted) {
                // Mount element attributes
                this.mount(context);
            }
        });
    }
    declareAttribute({ name, change }) {
        this.attrs.set(name, mergeFunctions(this.attrs.get(name), change));
    }
    define(elementType) {
        const { attrs } = this;
        if (!attrs.size) {
            return; // No attributes defined
        }
        Reflect.defineProperty(elementType, 'observedAttributes', {
            configurable: true,
            enumerable: true,
            value: observedAttributes(elementType, [...attrs.keys()]),
        });
        Reflect.defineProperty(elementType.prototype, 'attributeChangedCallback', {
            configurable: true,
            enumerable: true,
            value: attributeChangedCallback(elementType, attrs),
        });
    }
    mount(context) {
        const { element } = context;
        const { attrs } = this;
        const attributeFilter = [...attrs.keys()];
        if (!attributeFilter.length) {
            return; // No attributes defined
        }
        const MutationObserver = this._defContext.get(CxWindow).MutationObserver;
        const observer = new MutationObserver(records => records.forEach(record => {
            const attributeName = record.attributeName;
            return attrs.get(attributeName)(context.component, element.getAttribute(attributeName), record.oldValue);
        }));
        observer.observe(element, {
            attributes: true,
            attributeFilter,
            attributeOldValue: true,
        });
    }
}
/**
 * @internal
 */
function observedAttributes(elementType, attrs) {
    const alreadyObserved = elementType.observedAttributes;
    if (Array.isArray(alreadyObserved)) {
        const newAttrs = new Set(alreadyObserved);
        attrs.forEach(attr => newAttrs.add(attr));
        attrs = [...newAttrs];
    }
    return attrs;
}
/**
 * @internal
 */
function attributeChangedCallback(elementType, attrs) {
    const prevCallback = elementType.prototype.attributeChangedCallback;
    if (!prevCallback) {
        return function (name, oldValue, newValue) {
            ComponentSlot.of(this).whenReady(({ component }) => {
                attrs.get(name)(component, newValue, oldValue);
            });
        };
    }
    return function (name, oldValue, newValue) {
        const attrChanged = attrs.get(name);
        if (attrChanged) {
            ComponentSlot.of(this).whenReady(({ component }) => attrChanged(component, newValue, oldValue));
        }
        else {
            prevCallback.call(this, name, oldValue, newValue);
        }
    };
}

/**
 * Creates an amendment (and decorator) of component property that accesses custom element's attribute.
 *
 * The amended property accesses corresponding attribute on read, and updates it on setting. `null` value corresponds
 * to absent attribute. Setting to `null` or `undefined` removes corresponding attribute.
 *
 * @category Feature
 * @typeParam TClass - Amended component class type.
 * @typeParam TAmended - Amended component member entity type.
 * @param def - Attribute definition or just an attribute name (either _camelCase_ or _dash-style_).
 *
 * @return New component property amendment.
 */
function Attribute(def) {
    return ComponentMember(({ amendedClass, key, set: setValue, amend, }) => {
        const { name, change } = parseAttributeDescriptor(amendedClass.prototype, key, def);
        amend({
            componentDef: {
                define(defContext) {
                    defContext.get(AttributeRegistry).declareAttribute({
                        name,
                        change(component, newValue, oldValue) {
                            setValue(component, newValue);
                            change(component, newValue, oldValue);
                        },
                    });
                },
            },
            get(component) {
                return ComponentContext.of(component).element.getAttribute(name);
            },
            set(component, newValue) {
                const { element } = ComponentContext.of(component);
                if (newValue != null) {
                    element.setAttribute(name, newValue);
                }
                else {
                    element.removeAttribute(name);
                }
                setValue(component, newValue);
            },
        });
    });
}

/**
 * Creates a component method amendment (and decorator) for custom element attribute {@link AttributeDef.ChangeMethod
 * change callback}.
 *
 * Example:
 * ```TypeScript
 * @Component('my-component')
 * class MyComponent {
 *
 *   @AttributeChanged('my-attribute')
 *   myAttributeChanged(newValue: string, oldValue: string | null) {
 *     console.log(`my-attribute value changed from ${oldValue} to ${newValue}`);
 *   }
 *
 * }
 * ```
 *
 * @category Feature
 * @typeParam TClass - Amended component class type.
 * @typeParam TAmended - Amended component member entity type.
 * @param def - Attribute definition or just an attribute name.
 *
 * @return New component method amendment.
 */
function AttributeChanged(def) {
    return ComponentMember(({ amendedClass, get, key, amend }) => {
        const { name, change } = parseAttributeDescriptor(amendedClass.prototype, key, def);
        amend({
            componentDef: {
                define(defContext) {
                    defContext.get(AttributeRegistry).declareAttribute({
                        name,
                        change(component, newValue, oldValue) {
                            const callback = get(component);
                            callback.call(component, newValue, oldValue);
                            change(component, newValue, oldValue);
                        },
                    });
                },
            },
        });
    });
}

/**
 * Creates a component amendment (and decorator) that declares supported attributes of component's element.
 *
 * @category Feature
 * @typeParam TClass - Amended component class type.
 * @typeParam TAmended - Amended component entity type.
 * @param items - Attributes definition options.
 *
 * @returns New component amendment and decorator.
 */
function Attributes(...items) {
    return Component({
        define(defContext) {
            const registry = defContext.get(AttributeRegistry);
            for (const item of items) {
                if (typeof item === 'string') {
                    const name = hyphenateDecapName(item);
                    registry.declareAttribute({
                        name,
                        change: attributeStateUpdate(name),
                    });
                }
                else {
                    for (const [key, updateState] of Object.entries(item)) {
                        const name = hyphenateDecapName(key);
                        registry.declareAttribute({
                            name,
                            change: attributeStateUpdate(name, updateState),
                        });
                    }
                }
            }
        },
    });
}

/**
 * Component state tracker.
 *
 * @category Feature
 */
class ComponentState extends StateTracker {
    static perContext(target) {
        return {
            assign: ComponentState$assign(target, target => target.recentAsset),
            assignDefault: ComponentState$assign(target, _target => new ComponentState()),
        };
    }
    static toString() {
        return '[ComponentState]';
    }
}
function ComponentState$assign(target, getState) {
    const get = target.lazy(target => {
        const state = getState(target);
        if (state) {
            target.provide(cxConstAsset(StateUpdater, state.update));
        }
        return state;
    });
    return receiver => {
        const state = get();
        if (state) {
            target.supply.whenOff(reason => state.done(reason));
            receiver(state);
        }
        return state;
    };
}

/**
 * A path to sub-state containing component state properties.
 *
 * Thus a property state path is always something like `[StatePropertyPath__root, 'property-name']`.
 *
 * @category Feature
 */
const StatePropertyPath__root = ( /*#__PURE__*/Symbol('StateProperty'));
/**
 * Constructs a named component property state path.
 *
 * @category Feature
 * @typeParam TKey - Target property key type.
 * @param key - Target property key.
 *
 * @return DOM property state path.
 */
function statePropertyPathTo(key) {
    return [StatePropertyPath__root, key];
}

/**
 * @internal
 */
function statePropertyUpdate(propertyKey, updateState = true) {
    if (updateState === true || typeof updateState === 'function') {
        const path = statePropertyPathTo(propertyKey);
        const update = updateState === true ? updateStatePropertyState : updateState;
        return (component, newValue, oldValue) => update(component, path, newValue, oldValue);
    }
    return (component, newValue, oldValue) => {
        if (newValue !== oldValue) {
            ComponentContext.of(component).updateState(updateState, newValue, oldValue);
        }
    };
}
function updateStatePropertyState(component, path, newValue, oldValue) {
    if (newValue !== oldValue) {
        ComponentContext.of(component).updateState(path, newValue, oldValue);
    }
}

/**
 * Creates an amendment (and decorator) of component member representing a property of component state.
 *
 * Once such property updated, the component state is {@link ComponentContext.updateState also updated}.
 *
 * @category Feature
 * @typeParam TValue - Amended member value type
 * @typeParam TClass - Amended component class type.
 * @typeParam TUpdate - Amended member update type accepted by its setter.
 * @typeParam TAmended - Amended component member entity type.
 * @param def - Custom element property definition.
 *
 * @returns New component member decorator.
 */
function StateProperty({ updateState } = {}) {
    return ComponentMember(({ key, get, set, amend }) => {
        if (updateState !== false) {
            const update = statePropertyUpdate(key, updateState);
            amend({
                get,
                set(component, newValue) {
                    const oldValue = get(component);
                    set(component, newValue);
                    update(component, newValue, oldValue);
                },
            });
        }
    });
}

class StatePropertyTracker extends ValueTracker {
    constructor(_context, key, path) {
        super();
        this._context = _context;
        this.supply = new Supply();
        this._key = key;
        this.on = _context.get(ComponentState).track(path).onUpdate.do(translateOn((send, _path, newValue, oldValue) => send(newValue, oldValue)), supplyOn(this));
    }
    get it() {
        return this._context.component[this._key];
    }
    set it(value) {
        if (!this.supply.isOff) {
            this._context.component[this._key] = value;
        }
    }
}
/**
 * Creates a tracker of component state value.
 *
 * @category Feature
 * @typeParam T - A type of state property value.
 * @param context - Target component context.
 * @param key - Property key.
 * @param path - Property state path.
 *
 * @returns New state property value tracker.
 */
function trackStateProperty(context, key, path = statePropertyPathTo(key)) {
    return new StatePropertyTracker(context, key, path);
}

class AttributeTracker extends ValueTracker {
    constructor(_context, _name, path) {
        super();
        this._context = _context;
        this._name = _name;
        this.supply = new Supply();
        this.on = this._context.get(ComponentState).track(path).onUpdate.do(translateOn((send, _path, newValue, oldValue) => send(newValue, oldValue)), supplyOn(this));
    }
    get it() {
        return this._context.element.getAttribute(this._name);
    }
    set it(value) {
        if (!this.supply.isOff) {
            if (value == null) {
                this._context.element.removeAttribute(this._name);
            }
            else {
                this._context.element.setAttribute(this._name, value);
            }
        }
    }
}
/**
 * Creates a tracker of custom element's attribute value.
 *
 * Requires attribute to be defined. E.g. with {@link Attribute @Attribute}, {@link AttributeChanged @AttributeChanged},
 * or {@link Attributes @Attributes} decorator.
 *
 * @category Feature
 * @param context - Target component context.
 * @param name - Attribute name.
 * @param path - Custom attribute state path.
 *
 * @returns New attribute value tracker.
 */
function trackAttribute(context, name, path = attributePathTo(name)) {
    return new AttributeTracker(context, name, path);
}

/**
 * @internal
 */
function domPropertyDescriptor(amended, { propertyKey: key = amended.key, configurable = amended.configurable, enumerable = amended.enumerable, writable = amended.writable, }) {
    const componentMemberKey = amended.key;
    const descriptor = {
        configurable,
        enumerable,
        get: function () {
            var _a;
            return (_a = ComponentSlot.of(this).rebind()) === null || _a === void 0 ? void 0 : _a.component[componentMemberKey];
        },
        set: writable
            ? function (value) {
                ComponentSlot.of(this).whenReady(({ component, }) => component[componentMemberKey] = value);
            }
            : undefined,
    };
    return { key, descriptor };
}

/**
 * Component definition context entry containing {@link DomPropertyRegistry DOM property registry}.
 *
 * @category Feature
 */
const DomPropertyRegistry = {
    perContext: ( /*#__PURE__*/cxScoped(DefinitionContext, ( /*#__PURE__*/cxSingle({
        byDefault: target => new DomPropertyRegistry$(target.get(DefinitionContext)),
    })))),
    toString: () => '[DomPropertyRegistry]',
};
class DomPropertyRegistry$ {
    constructor(defContext) {
        this.props = new Map();
        defContext.whenReady(({ elementType }) => this.define(elementType));
        defContext.whenComponent(context => {
            if (context.mounted) {
                // Mount element properties
                this.mount(context);
            }
        });
    }
    declareDomProperty({ key, descriptor }) {
        this.props.set(key, descriptor);
    }
    define(elementType) {
        const prototype = elementType.prototype;
        this.props.forEach((desc, key) => {
            Reflect.defineProperty(prototype, key, desc);
        });
    }
    mount({ element }) {
        this.props.forEach((desc, key) => {
            Reflect.defineProperty(element, key, desc);
        });
    }
}

/**
 * A path to sub-state containing DOM properties.
 *
 * Thus a property state path is always something like `[DomPropertyPath__root, 'property-name']`.
 *
 * @category Feature
 */
const DomPropertyPath__root = ( /*#__PURE__*/Symbol('DomProperty'));
/**
 * Constructs a named DOM property state path.
 *
 * @category Feature
 * @typeParam TKey - Property key type.
 * @param key - Target property key.
 *
 * @return DOM property state path.
 */
function domPropertyPathTo(key) {
    return [DomPropertyPath__root, key];
}

/**
 * @internal
 */
function domPropertyUpdate(propertyKey, updateState = true) {
    if (updateState === true || typeof updateState === 'function') {
        const path = domPropertyPathTo(propertyKey);
        const update = updateState === true ? updateDomPropertyState : updateState;
        return (component, newValue, oldValue) => update(component, path, newValue, oldValue);
    }
    return (component, newValue, oldValue) => {
        if (newValue !== oldValue) {
            ComponentContext.of(component).updateState(updateState, newValue, oldValue);
        }
    };
}
function updateDomPropertyState(component, path, newValue, oldValue) {
    if (newValue !== oldValue) {
        ComponentContext.of(component).updateState(path, newValue, oldValue);
    }
}

/**
 * Creates component member amendment (and decorator) that declares a property to add to component's element.
 *
 * The value of declared element's property will be read from and written to decorated member.
 *
 * By default does not update component state if property value didn't change.
 *
 * @category Feature
 * @typeParam TValue - Amended member value type
 * @typeParam TClass - Amended component class type.
 * @typeParam TUpdate - Amended member update type accepted by its setter.
 * @typeParam TAmended - Amended component member entity type.
 * @param def - Custom element property definition.
 *
 * @returns New component member decorator.
 */
function DomProperty(def = {}) {
    return ComponentMember((target) => {
        const { key, get, amend } = target;
        let { set } = target;
        const domDescriptor = domPropertyDescriptor(target, def);
        if (def.updateState !== false) {
            const updateState = domPropertyUpdate(key, def.updateState);
            const setValue = set;
            set = (component, newValue) => {
                const oldValue = get(component);
                setValue(component, newValue);
                updateState(component, newValue, oldValue);
            };
        }
        amend({
            componentDef: {
                define(defContext) {
                    defContext.get(DomPropertyRegistry).declareDomProperty(domDescriptor);
                },
            },
            get,
            set,
        });
    });
}

/**
 * @internal
 */
class DomPropertyTracker extends ValueTracker {
    constructor(_context, key, _path) {
        super();
        this._context = _context;
        this._path = _path;
        this.supply = new Supply();
        this._key = key;
        this.on = this._context.get(ComponentState).track(this._path).onUpdate.do(translateOn((send, _path, newValue, oldValue) => send(newValue, oldValue)), supplyOn(this));
    }
    get it() {
        return this._context.element[this._key];
    }
    set it(value) {
        if (!this.supply.isOff) {
            this._context.element[this._key] = value;
        }
    }
}
/**
 * Creates a tracker of custom element's DOM property value.
 *
 * Requires property to be defined. E.g. with {@link DomProperty @DomProperty} decorator.
 *
 * @category Feature
 * @typeParam T - A type of DOM property value.
 * @param context - Target component context.
 * @param key - Property key.
 * @param path - Custom property state path.
 *
 * @returns New DOM property value tracker.
 */
function trackDomProperty(context, key, path = domPropertyPathTo(key)) {
    return new DomPropertyTracker(context, key, path);
}

/**
 * A root path to sub-states updates to which will be ignored by default.
 *
 * This can be used to create sub-states that won't trigger rendering occasionally, but only when requested explicitly.
 *
 * @category Feature
 */
const RenderPath__root = ( /*#__PURE__*/Symbol('render'));
/**
 * @category Feature
 */
const RenderDef = {
    /**
     * Builds a trigger issuing rendering updates.
     *
     * @param context - Rendered component context.
     * @param spec - Rendering specifier.
     *
     * @returns `OnEvent` sender that sends an event each time the rendering required.
     */
    trigger(context, spec = {}) {
        const { on = [] } = spec;
        if ((typeof on === 'object' || typeof on === 'function') && isEventSender(on)) {
            return onSupplied(on).do(supplyOn(context));
        }
        const trigger = context
            .get(ComponentState)
            .track(on)
            .onUpdate
            .do(supplyOn(context));
        if (Array.isArray(on) && !on.length) {
            return trigger.do(translateOn_((send, path) => path[0] !== RenderPath__root && send()));
        }
        return trigger;
    },
};

class ComponentRenderCtl$ {
    constructor(_context) {
        this._context = _context;
        const { element } = _context;
        this._scheduler = _context.get(DocumentRenderKit).contextOf(element).scheduler;
    }
    renderBy(renderer, def) {
        return new ComponentRenderer$State(this, renderer, def).render();
    }
    preRenderBy(preRenderer, def) {
        return new ComponentPreRenderer$State(this, preRenderer, def).render();
    }
}
class ComponentRenderer$BaseState {
    constructor(_ctl, _renderer, def = {}) {
        this._ctl = _ctl;
        this._renderer = _renderer;
        this._status = 1 /* Pending */;
        this._spec = valueByRecipe(def, _ctl._context);
    }
    render() {
        const context = this._ctl._context;
        const trigger = RenderDef.trigger(context, this._spec);
        let schedule = shot => {
            schedule = this._createSchedule();
            schedule(shot);
        };
        const whenConnected = this._spec.when === 'connected';
        const startRendering = () => this._status /* there is an update to render */
            && this._scheduleRenderer(schedule);
        const onUpdate = whenConnected
            ? () => context.connected && this._scheduleRenderer(schedule)
            : () => context.settled && this._scheduleRenderer(schedule);
        this._supply = trigger(onUpdate)
            .needs(context)
            .whenOff(() => this._cancel(schedule));
        (whenConnected ? context.whenConnected : context.whenSettled)(startRendering);
        return this._supply;
    }
    _scheduleRenderer(schedule) {
        this._status = 2 /* Scheduled */;
        schedule(execution => this._render(execution));
    }
    _createSchedule() {
        return this._scheduleBy(this._ctl._scheduler);
    }
    _scheduleBy(scheduler) {
        const node = this._ctl._context.element;
        const schedule = scheduler({ ...this._spec, node });
        return shot => schedule(execution => drekBuild(() => shot(execution)));
    }
    _render(execution) {
        const rendererExecution = this._createExecution(execution);
        this._status = 0 /* Complete */;
        do {
            const currentRenderer = this._renderer;
            currentRenderer(rendererExecution);
            if (this._renderer === currentRenderer) {
                this._over();
                break; // The renderer is not updated. Current renderer execution is over.
            }
        } while (this._status >= 0); // The rendering could be cancelled by the renderer itself.
    }
    _over() {
        // Renderer execution is over.
    }
    _cancel(schedule) {
        if (this._status === 2 /* Scheduled */) { // Scheduled, but not rendered yet.
            schedule(noop);
        }
        this._status = -1 /* Cancelled */;
    }
}
class ComponentRenderer$State extends ComponentRenderer$BaseState {
    _createExecution(execution) {
        const rendererExecution = {
            ...execution,
            postpone(postponed) {
                execution.postpone(() => postponed(rendererExecution));
            },
            supply: this._supply,
            renderBy: (renderer) => {
                this._renderer = renderer;
            },
        };
        return rendererExecution;
    }
}
const ComponentPreRenderer$done = {};
class ComponentPreRenderer$State extends ComponentRenderer$BaseState {
    constructor() {
        super(...arguments);
        this._nextRenderer = null;
    }
    render() {
        this._preSupply = new Supply();
        super.render().needs(this._preSupply).whenOff(reason => {
            if (reason === ComponentPreRenderer$done) {
                // Pre-rendering is over.
                // Delegate to component renderer.
                this._preSupply.as(this._ctl.renderBy(this._nextRenderer));
            }
            else {
                // Pre-rendering aborted.
                this._preSupply.off(reason);
            }
        });
        return this._preSupply;
    }
    _createSchedule() {
        const preScheduler = this._ctl._context.get(PreRenderScheduler);
        return this._scheduleBy(preScheduler);
    }
    _over() {
        if (this._nextRenderer) {
            // Signal the pre-rendering is over.
            this._supply.off(ComponentPreRenderer$done);
        }
    }
    _createExecution(execution) {
        const preRendererExecution = {
            ...execution,
            postpone: postponed => {
                execution.postpone(() => {
                    postponed(preRendererExecution);
                    this._over();
                });
            },
            supply: this._preSupply,
            renderBy: (renderer) => {
                this._renderer = renderer;
                this._nextRenderer = renderer;
            },
            preRenderBy: (preRenderer) => {
                this._renderer = preRenderer;
            },
        };
        return preRendererExecution;
    }
}

/**
 * Component context entry containing {@link ComponentRenderCtl component render control}.
 *
 * @category Feature
 */
const ComponentRenderCtl = {
    perContext: ( /*#__PURE__*/cxSingle({
        byDefault: target => new ComponentRenderCtl$(target.get(ComponentContext)),
    })),
    toString: () => '[ComponentRenderCtl]',
};

/**
 * Component context entry containing {@link ComponentRenderScheduler component render scheduler}.
 *
 * @category Feature
 */
const ComponentRenderScheduler = {
    perContext: ( /*#__PURE__*/cxSingle({
        byDefault: ComponentRenderScheduler$byDefault,
    })),
    toString: () => '[ComponentRenderScheduler]',
};
function ComponentRenderScheduler$byDefault(target) {
    const renderCtl = target.get(ComponentRenderCtl);
    return (opts = {}) => {
        const recentShot = trackValue(noop);
        const renderer = execution => {
            recentShot.it(execution);
        };
        renderCtl.renderBy(renderer, { ...opts, on: recentShot.on });
        return (shot) => {
            recentShot.it = execution => shot(execution); // Ensure render shot always updated
        };
    };
}

/**
 * Creates a {@link ComponentPreRenderer component pre-renderer} method amendment (and decorator).
 *
 * Enables pre-rendering with {@link ComponentRenderCtl.preRenderBy component render control}.
 *
 * The decorated method accepts a {@link ComponentPreRendererExecution component rendering context} as its only
 * parameter.
 *
 * @category Feature
 * @typeParam TClass - Amended component class type.
 * @typeParam TAmended - Amended component member entity type.
 * @param def - Non-mandatory render definition.
 *
 * @returns New component method amendment.
 */
function PreRender(def) {
    return ComponentMember(({ get, amend }) => amend({
        componentDef: {
            define(defContext) {
                defContext.whenComponent(context => {
                    context.whenReady(() => {
                        const { component } = context;
                        const preRenderer = get(component).bind(component);
                        context.get(ComponentRenderCtl).preRenderBy(preRenderer, def);
                    });
                });
            },
        },
    }));
}

/**
 * Creates a {@link ComponentRenderer component renderer} method amendment (and decorator).
 *
 * Enables rendering with {@link ComponentRenderCtl.renderBy component render control}.
 *
 * The decorated method accepts a {@link ComponentRendererExecution component rendering context} as its only parameter.
 *
 * @category Feature
 * @typeParam TClass - Amended component class type.
 * @typeParam TAmended - Amended component member entity type.
 * @param def - Non-mandatory render definition.
 *
 * @returns New component method amendment.
 */
function Render(def) {
    return ComponentMember(({ get, amend }) => amend({
        componentDef: {
            define(defContext) {
                defContext.whenComponent(context => {
                    context.whenReady(() => {
                        const { component } = context;
                        const renderer = get(component).bind(component);
                        context.get(ComponentRenderCtl).renderBy(renderer, def);
                    });
                });
            },
        },
    }));
}

/**
 * Component context entry containing a shadow content root instance.
 *
 * This is only available when the component is decorated with {@link AttachShadow @AttachShadow} decorator.
 *
 * @category Feature
 */
const ShadowContentRoot = {
    perContext: ( /*#__PURE__*/cxScoped(ComponentContext, ( /*#__PURE__*/cxSingle()))),
    toString: () => '[ShadowContentRoot]',
};

/**
 * Context entry containing shadow root builder instance.
 *
 * @category Feature
 */
const ShadowRootBuilder = {
    perContext: ( /*#__PURE__*/cxRecent({
        create: (recent, _target) => recent,
        byDefault: _target => attachShadow,
        assign({ get, to }, _target) {
            const builder = (context, init) => get()(context, init);
            return receiver => to((_, by) => receiver(builder, by));
        },
    })),
    toString: () => '[ShadowRootBuilder]',
};
function attachShadow(context, init) {
    return shadowRootOf(context.element, init);
}
function shadowRootOf(element, init) {
    const existing = element.shadowRoot;
    if (existing) {
        // Shadow root already attached. Using it.
        return existing;
    }
    if ('attachShadow' in element) {
        return element.attachShadow(init);
    }
    return; // Unable to attach shadow root.
}

/**
 * @internal
 */
const defaultShadowContentDef = { mode: 'open' };
/**
 * Creates a component amendment (and decorator) that attaches shadow root to decorated component instance.
 *
 * @category Feature
 * @typeParam TClass - Amended component class type.
 * @typeParam TAmended - Amended component entity type.
 * @param def - Shadow content root definition. Uses `mode: 'open'` by default.
 *
 * @returns New component amendment and decorator.
 */
function AttachShadow(def = defaultShadowContentDef) {
    return Component({
        setup(setup) {
            setup.perComponent(cxBuildAsset(ShadowContentRoot, target => target.get(ShadowRootBuilder)(target.get(ComponentContext), def)));
            setup.perComponent(cxBuildAsset(// Content root is an alias of shadow root when present.
            ContentRoot, target => target.get(ShadowContentRoot, { or: null })));
        },
    });
}

export { AttachShadow, Attribute, AttributeChanged, AttributePath__root, AttributeRegistry, Attributes, BootstrapContext, BootstrapRoot, Component, ComponentContext, ComponentContext__symbol, ComponentDef, ComponentDef__symbol, ComponentEventDispatcher, ComponentMember, ComponentRenderCtl, ComponentRenderScheduler, ComponentSlot, ComponentSlot__symbol, ComponentState, ContentRoot, CustomElements, DefinitionContext, DomProperty as DomMethod, DomProperty, DomPropertyPath__root, DomPropertyRegistry, ElementNaming, Feature, FeatureContext, FeatureDef, FeatureDef__symbol, PreRender, Render, RenderDef, RenderPath__root, ShadowContentRoot, ShadowRootBuilder, StateProperty, StatePropertyPath__root, StateUpdater, Wesib__NS, attributePathTo, bootstrapComponents, domPropertyPathTo, statePropertyPathTo, trackAttribute, trackDomProperty, trackStateProperty };
//# sourceMappingURL=wesib.js.map
